import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ClearIcon from "@mui/icons-material/Clear";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import "dayjs/locale/it";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const todayStart = () => dayjs().startOf("day");

function DateRangeQuickPresets({ setStart, setEnd, setError }) {
  const clearSelection = () => {
    setStart(null);
    setEnd(null);
    setError("");
  };

  const setPreset = (days) => {
    const e = todayStart();
    const s = e.subtract(days - 1, "day");
    setStart(s);
    setEnd(e);
    setError("");
  };

  return (
    <Box>
      <Typography sx={{ fontFamily: "Rubik", fontWeight: 600, color: "#334155", fontSize: 12, letterSpacing: "0.04em", mb: 1 }}>
        INTERVALLO RAPIDO
      </Typography>
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
        <Chip
          label="Ultimi 7 giorni"
          size="small"
          onClick={() => setPreset(7)}
          sx={{ fontFamily: "Rubik", fontWeight: 500, borderRadius: "8px" }}
        />
        <Chip
          label="Ultimi 30 giorni"
          size="small"
          onClick={() => setPreset(30)}
          sx={{ fontFamily: "Rubik", fontWeight: 500, borderRadius: "8px" }}
        />
        <Chip
          label="Mese corrente"
          size="small"
          onClick={() => {
            const s = dayjs().startOf("month");
            const e = todayStart();
            setStart(s);
            setEnd(e);
            setError("");
          }}
          sx={{ fontFamily: "Rubik", fontWeight: 500, borderRadius: "8px" }}
        />
        <IconButton
          type="button"
          aria-label="Azzera selezione calendario"
          size="small"
          onClick={clearSelection}
          sx={{
            border: "1px solid rgba(15, 23, 42, 0.12)",
            borderRadius: "8px",
            color: "#64748b",
          }}
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

DateRangeQuickPresets.propTypes = {
  setStart: PropTypes.func.isRequired,
  setEnd: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
};

function dayRangeFlags(day, rangeStart, rangeEnd) {
  const d = day.startOf("day");
  const s = rangeStart?.startOf("day") ?? null;
  const e = rangeEnd?.startOf("day") ?? null;
  const hasRange = Boolean(s && e);
  const inRange = hasRange && d.isSameOrAfter(s, "day") && d.isSameOrBefore(e, "day");
  const isStart = Boolean(s && d.isSame(s, "day"));
  const isEnd = Boolean(e && d.isSame(e, "day"));
  return { inRange, isStart, isEnd, hasRange };
}

const DateRangePickerDialog = ({
  open,
  onClose,
  initialStart,
  initialEnd,
  onApply,
  title = "Periodo",
  showQuickPresets = false,
  anchorEl = null,
}) => {
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      dayjs.locale("it");
      setStart(initialStart ?? null);
      setEnd(initialEnd ?? null);
      setError("");
    }
  }, [open, initialStart, initialEnd]);

  const handleCalendarChange = useCallback(
    (clicked) => {
      if (!clicked) return;
      const d = clicked.startOf("day");
      const t = todayStart();
      if (d.isAfter(t, "day")) return;
      setError("");
      if (start && end) {
        setStart(d);
        setEnd(null);
        return;
      }
      if (!start) {
        setStart(d);
        setEnd(null);
        return;
      }
      if (d.isBefore(start, "day")) {
        setEnd(start);
        setStart(d);
      } else {
        setEnd(d);
      }
    },
    [start, end]
  );

  const applyRange = useCallback(
    (nextStart, nextEnd) => {
      if (!nextStart && !nextEnd) {
        onApply({ start: null, end: null });
        onClose();
        return;
      }
      if (!nextStart || !nextEnd) {
        setError("Seleziona due date sul calendario (inizio e fine) oppure azzera la selezione.");
        return;
      }
      if (nextEnd.isBefore(nextStart, "day")) {
        setError("La data fine non può essere precedente alla data inizio.");
        return;
      }
      const t = todayStart();
      if (nextStart.isAfter(t, "day") || nextEnd.isAfter(t, "day")) {
        setError("Le date non possono essere successive a oggi.");
        return;
      }
      setError("");
      onApply({ start: nextStart.startOf("day"), end: nextEnd.endOf("day") });
      onClose();
    },
    [onApply, onClose]
  );

  const handleApplyClick = () => applyRange(start, end);

  const RangeDay = useMemo(() => {
    const RangePickersDay = React.forwardRef(function RangePickersDayInner(dayProps, ref) {
      const { day, outsideCurrentMonth, sx: sxProp, ...other } = dayProps;
      const { inRange, isStart, isEnd } = dayRangeFlags(day, start, end);
      const singleDayRange = isStart && isEnd;
      return (
        <PickersDay
          {...other}
          ref={ref}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          disableMargin={Boolean(inRange && !singleDayRange)}
          selected={isStart || isEnd}
          sx={[
            ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
            !outsideCurrentMonth &&
              inRange &&
              !singleDayRange && {
                borderRadius: 0,
                backgroundColor: "rgba(11, 61, 135, 0.12)",
                color: "#0f172a",
              },
            !outsideCurrentMonth &&
              (isStart || isEnd) && {
                backgroundColor: "#0b3d87",
                color: "#fff",
                "&:hover, &:focus": { backgroundColor: "#092b6a" },
              },
            !outsideCurrentMonth && isStart && !singleDayRange && { borderRadius: "50% 0 0 50%" },
            !outsideCurrentMonth && isEnd && !singleDayRange && { borderRadius: "0 50% 50% 0" },
          ]}
        />
      );
    });
    return RangePickersDay;
  }, [start, end]);

  const isPopover = Boolean(anchorEl);

  const panelPaperSx = {
    borderRadius: isPopover ? "12px" : "16px",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow: isPopover ? "0 12px 32px rgba(15, 23, 42, 0.14)" : "0 25px 50px -12px rgba(15, 23, 42, 0.18)",
    overflow: "hidden",
    width: isPopover ? 380 : undefined,
    maxWidth: isPopover ? "calc(100vw - 16px)" : undefined,
  };

  const calendarSection = (
    <Box sx={{ px: 2.5, pt: isPopover ? 1.5 : 2.5, pb: isPopover ? 1.5 : 2 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
        <Stack spacing={2}>
          {showQuickPresets ? <DateRangeQuickPresets setStart={setStart} setEnd={setEnd} setError={setError} /> : null}

          <Box sx={{ display: "flex", justifyContent: "center", "& .MuiDateCalendar-root": { width: "100%", maxWidth: 360 } }}>
            <DateCalendar
              disableFuture
              value={null}
              referenceDate={start ?? end ?? dayjs()}
              onChange={handleCalendarChange}
              slots={{ day: RangeDay }}
              sx={{
                fontFamily: "Rubik",
                "& .MuiPickersCalendarHeader-label": { fontFamily: "Rubik", fontWeight: 600 },
              }}
            />
          </Box>

          {error ? (
            <Typography sx={{ fontFamily: "Rubik", color: "#c62828", fontSize: 13 }} role="alert">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </LocalizationProvider>
    </Box>
  );

  const footerActions = (
    <Box
      sx={{
        px: 2.5,
        py: isPopover ? 1.5 : 2,
        borderTop: "1px solid rgba(15, 23, 42, 0.06)",
        backgroundColor: isPopover ? "#ffffff" : "#fafafa",
        display: "flex",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Button
        onClick={onClose}
        sx={{
          fontFamily: "Rubik",
          textTransform: "none",
          color: "#475569",
          fontWeight: 600,
        }}
      >
        Annulla
      </Button>
      <Button
        variant="contained"
        onClick={handleApplyClick}
        sx={{
          fontFamily: "Rubik",
          textTransform: "none",
          fontWeight: 700,
          borderRadius: "10px",
          px: 2.5,
          backgroundColor: "#0b3d87",
          boxShadow: "none",
          "&:hover": { backgroundColor: "#092b6a", boxShadow: "none" },
        }}
      >
        Applica
      </Button>
    </Box>
  );

  const dialogHeader = (
    <Box
      sx={{
        px: 2.5,
        pt: 2.5,
        pb: 1.5,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
        borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            backgroundColor: "rgba(11, 61, 135, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CalendarMonthOutlined sx={{ color: "#0b3d87", fontSize: 26 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: "Rubik", fontWeight: 700, color: "#0f172a", fontSize: "1.125rem", lineHeight: 1.3 }}>
            {title}
          </Typography>
        </Box>
      </Stack>
      <IconButton
        aria-label="Chiudi"
        onClick={onClose}
        size="small"
        sx={{
          color: "#64748b",
          backgroundColor: "rgba(15, 23, 42, 0.04)",
          "&:hover": { backgroundColor: "rgba(15, 23, 42, 0.08)" },
        }}
      >
        <CloseRounded fontSize="small" />
      </IconButton>
    </Box>
  );

  const panelBody = isPopover ? (
    <>
      {calendarSection}
      {footerActions}
    </>
  ) : (
    <>
      {dialogHeader}
      {calendarSection}
      {footerActions}
    </>
  );

  if (isPopover) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        marginThreshold={8}
        slotProps={{
          paper: {
            elevation: 0,
            sx: panelPaperSx,
          },
        }}
      >
        {panelBody}
      </Popover>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(15, 23, 42, 0.45)" } },
      }}
      PaperProps={{
        elevation: 0,
        sx: panelPaperSx,
      }}
    >
      {panelBody}
    </Dialog>
  );
};

DateRangePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialStart: PropTypes.object,
  initialEnd: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  title: PropTypes.string,
  showQuickPresets: PropTypes.bool,
  anchorEl: PropTypes.object,
};

export default DateRangePickerDialog;
